// Pure reducer for the per-user delta feed (Phase 7). Kept free of Pinia/idb/
// network so it can be unit-tested and reasoned about in isolation.

export type EntityField =
  | 'userAdmins'
  | 'userReleaseEntryStatuses'
  | 'userCustomLists'
  | 'userCustomListReleaseEntryItems'
  | 'userCustomListNoveltyItems'

// The delta field name on UserDeltaPage -> the entity name the tombstones use.
export const FIELD_TO_ENTITY: Record<EntityField, string> = {
  userAdmins: 'UserAdmin',
  userReleaseEntryStatuses: 'UserReleaseEntryStatus',
  userCustomLists: 'UserCustomList',
  userCustomListReleaseEntryItems: 'UserCustomListReleaseEntryItem',
  userCustomListNoveltyItems: 'UserCustomListNoveltyItem',
}

export type Row = { id: string }
export type EntityRows = Record<string, Row>
export const USER_SYNC_SCHEMA_VERSION = 2

export type UserSyncData = {
  userId: string | null
  schemaVersion?: number
  cursor: string
  rows: Record<string, EntityRows>
}

export type Tombstone = { entity: string; rowId: string }
// Structural shape of a UserDeltaPage — only what the reducer reads. The
// generated GetUserChangesQuery page is assignable to this.
export type ApplyablePage = {
  tombstones: readonly Tombstone[]
  nextSeq: string
} & Partial<Record<EntityField, readonly Row[]>>

export function emptyData(userId: string | null): UserSyncData {
  return { userId, schemaVersion: USER_SYNC_SCHEMA_VERSION, cursor: '0', rows: {} }
}

export function isCurrentSchema(data: UserSyncData): boolean {
  return data.schemaVersion === USER_SYNC_SCHEMA_VERSION
}

export function entityRows(data: UserSyncData, entity: string): Row[] {
  return Object.values(data.rows[entity] ?? {})
}

// Fold one delta page into the store: upsert each present entity's rows by id,
// then apply tombstones (deletes), then advance the cursor. Mutates `data`.
export function applyPage(data: UserSyncData, page: ApplyablePage): void {
  for (const field of Object.keys(FIELD_TO_ENTITY) as EntityField[]) {
    const rows = page[field]
    if (!rows) continue
    const entity = FIELD_TO_ENTITY[field]
    const bucket = data.rows[entity] ?? {}
    for (const row of rows) {
      bucket[row.id] = row
    }
    data.rows[entity] = bucket
  }
  for (const tombstone of page.tombstones) {
    delete data.rows[tombstone.entity]?.[tombstone.rowId]
  }
  data.cursor = page.nextSeq
}
